import type { JournalMirrorRequest } from './serviceJournal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER AUSGANGSKORB DER ZWEITSCHRIFT — Vormerkungen ueberleben die Sitzung
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 07.09.2026 lebte die vorgemerkte Zweitschrift ins Buchungsjournal
 * (`queueJournalMirror`) nur im Speicher der Sitzung. Die Offline-Warteschlange
 * (`offlineQueue.ts`, IndexedDB) schob den JSON-Stand aber auch NACH einem
 * Neuladen, Neustart oder Kontowechsel hoch — die Vormerkung war da laengst
 * weg. Folge: Protokollzeile im JSON, keine Journalzeile, und `nur_journal`
 * kann einen spaeteren Verlust dieser Zeile nie melden, weil es nie eine
 * Journalzeile gab. Dieselbe Luecke traf den direkten Pfad: das Anhaengen
 * nach dem Versand lief ohne `await`, und wer die Seite schloss oder dessen
 * RPC still scheiterte, hinterliess dasselbe Bild (6051 #135, 6169 #24,
 * 6293 #17, 26051023 #7, 6195 #21 — alle innerhalb einer Woche).
 *
 * Hier liegt deshalb jede Zweitschrift, bevor sie geschrieben wird:
 *
 *   `vorgemerkt`  — der JSON-Stand ist noch NICHT bestaetigt gespeichert
 *                   (Muster B: der Pfad ruft nur `onUpdate`, die Warteschlange
 *                   schreibt spaeter). Geschrieben wird erst nach dem Flush
 *                   dieses Projekts — oder, wenn die Warteschlange fuer das
 *                   Projekt leer ist, nach einer Ruecksprache mit dem Server.
 *   `faellig`     — der JSON-Stand IST gespeichert, nur das Anhaengen steht
 *                   noch aus (Muster A vor der RPC, oder ein Fehlschlag).
 *
 * ─── Die eine Regel, die alles ordnet ───────────────────────────────────────
 * Das JSON fuehrt. Ein Eintrag aus diesem Korb wird nur dann angehaengt, wenn
 * das Protokoll des Projekts die Zeile HEUTE traegt: der Flush reicht seinen
 * geschriebenen Stand mit, der Wiederholungslauf fragt den Server. Was das
 * JSON nicht (mehr) kennt, wird verworfen — eine Journalzeile ohne JSON-Zeile
 * ist in der Uebergangsphase nicht zuruecknehmbar (kein Update, kein Delete).
 *
 * ─── Warum eine EIGENE Datenbank statt eines zweiten Stores in
 *     `osia_offline_v1` ────────────────────────────────────────────────────
 * Ein zweiter Store hiesse Versionssprung von `osia_offline_v1`. Ein offener
 * Tab mit der alten Version blockiert das Upgrade (`onblocked`), und der
 * bestehende Adapter behandelt `onversionchange` nicht — die Warteschlange des
 * anderen Tabs stuende dann still, ohne Meldung. Eine eigene Datenbank hat
 * dieses Problem nicht.
 *
 * ─── Ausfallrichtung ────────────────────────────────────────────────────────
 * Jeder Zugriff kann scheitern (privates Fenster, gesperrte Site-Daten,
 * Node-Tests ohne IndexedDB). Dann arbeitet `serviceJournal.ts` wie vor dem
 * 07.09.: nur im Speicher. Der Korb ist eine Verbesserung, keine Bedingung —
 * kein Fehler von hier darf einen Speichervorgang oder einen Versand
 * beeinflussen.
 */

export type JournalMirrorOutboxState = 'vorgemerkt' | 'faellig';

export interface JournalMirrorOutboxEntry {
    /** Eigene Kennung des Korbeintrags (nicht die einer Protokollzeile). */
    id: string;
    projectId: string;
    /** Wer vorgemerkt hat; `null`, wenn die Sitzung nicht lesbar war. */
    userId: string | null;
    state: JournalMirrorOutboxState;
    request: JournalMirrorRequest;
    createdAt: string;
    attempts: number;
    lastError: string | null;
}

/** Die Ablage — austauschbar, damit Tests ohne IndexedDB laufen. */
export interface JournalMirrorOutboxStore {
    put(entry: JournalMirrorOutboxEntry): Promise<void>;
    get(id: string): Promise<JournalMirrorOutboxEntry | undefined>;
    delete(id: string): Promise<void>;
    listByProject(projectId: string): Promise<JournalMirrorOutboxEntry[]>;
    listAll(): Promise<JournalMirrorOutboxEntry[]>;
}

const DB_NAME = 'osia_journal_mirror_v1';
const STORE = 'mirrors';

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: 'id' });
                store.createIndex('projectId', 'projectId');
                store.createIndex('state', 'state');
            }
        };
        request.onsuccess = () => {
            request.result.onclose = () => { dbPromise = null; };
            request.result.onversionchange = () => { request.result.close(); dbPromise = null; };
            resolve(request.result);
        };
        request.onerror = () => { dbPromise = null; reject(request.error); };
        request.onblocked = () => { /* nur bei Upgrades, und es gibt nur Version 1 */ };
    });
    return dbPromise;
};

const tx = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T> => {
    const db = await openDb();
    return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const store = transaction.objectStore(STORE);
        let result: T;
        const request = run(store);
        if (request) request.onsuccess = () => { result = request.result; };
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
};

const indexedDbStore: JournalMirrorOutboxStore = {
    put: entry => tx('readwrite', store => { store.put(entry); }),
    get: id => tx('readonly', store => store.get(id) as IDBRequest<JournalMirrorOutboxEntry | undefined>),
    delete: id => tx('readwrite', store => { store.delete(id); }),
    listByProject: async projectId =>
        (await tx('readonly', store => store.index('projectId').getAll(projectId) as IDBRequest<JournalMirrorOutboxEntry[]>)) || [],
    listAll: async () =>
        (await tx('readonly', store => store.getAll() as IDBRequest<JournalMirrorOutboxEntry[]>)) || [],
};

/**
 * Eine Ablage im Speicher — fuer Tests, und als Muster dafuer, was die
 * IndexedDB-Ablage zusichert (Kopie beim Schreiben, keine geteilten Objekte).
 */
export const createMemoryOutboxStore = (): JournalMirrorOutboxStore & { size(): number } => {
    const rows = new Map<string, JournalMirrorOutboxEntry>();
    const kopie = (e: JournalMirrorOutboxEntry): JournalMirrorOutboxEntry => JSON.parse(JSON.stringify(e));
    return {
        put: async e => { rows.set(e.id, kopie(e)); },
        get: async id => { const e = rows.get(id); return e ? kopie(e) : undefined; },
        delete: async id => { rows.delete(id); },
        listByProject: async projectId => [...rows.values()].filter(e => e.projectId === projectId).map(kopie),
        listAll: async () => [...rows.values()].map(kopie),
        size: () => rows.size,
    };
};

let override: JournalMirrorOutboxStore | null | undefined;

/**
 * Die Ablage dieser Umgebung — oder `null`, wenn es keine gibt (dann arbeitet
 * `serviceJournal.ts` nur im Speicher, wie vor dem 07.09.2026).
 */
export const journalMirrorOutboxStore = (): JournalMirrorOutboxStore | null => {
    if (override !== undefined) return override;
    return typeof indexedDB === 'undefined' ? null : indexedDbStore;
};

/** Nur fuer Tests: Ablage vorgeben (`null` = keine Ablage, wie in Node). */
export const setJournalMirrorOutboxStoreForTests = (store: JournalMirrorOutboxStore | null | undefined): void => {
    override = store;
};
